import React, { useMemo, useState } from 'react';
import { Chart } from 'react-google-charts';
import { addDays, format, parseISO, startOfDay, subDays } from 'date-fns';
import { useCurrentEffect } from '../../../../../lib/use-current-effect';

import { AlertTrigger } from '../../../../../entities/Alert';
import { GraphData } from '../../../../../entities/GraphData';
import { HealthParameterThreshold } from '../../../../../entities/HealthParameter';
import { getMetricValuesByRange } from '../../../../../services/metrics';
import getAlertLabels from '../../../../../utils/getAlertLabels';
import {
  AlertChartConfig,
  LimitLine,
  limitLines,
  triggerAsMetric,
} from '../../../../../utils/alertChart';
import Spinner from '../../../../../components/Spinner';

import {
  Caption,
  Empty,
  Legend,
  LegendItem,
  Loading,
  Swatch,
  Wrapper,
} from './styles';

const WINDOW_DAYS = 7;
const SERIES_COLORS = ['#2D9CDB', '#30E3E5'];

interface MetricChartProps {
  patientId: string;
  parameter: string;
  config: AlertChartConfig;
  thresholds: HealthParameterThreshold[];
  /** O gráfico mostra os 7 dias que terminam nesta data (a última leitura do alerta). */
  anchor: string;
  /**
   * As leituras do próprio alerta. Se o metrics não devolver nada no período, o
   * gráfico desenha estas, e a legenda diz que são as do alerta.
   */
  fallback: AlertTrigger[];
}

/**
 * As últimas leituras do parâmetro, do metrics service, com os limites da régua como
 * linhas tracejadas.
 *
 * ⚠️ **O fim da janela é o dia seguinte, não hoje.** A rota compara só a parte de
 * data (`BETWEEN '2026-09-18' AND '2026-09-25'` sobre `mensuramentDate` completo), e
 * uma leitura de 25/09 às 10h é maior que `'2026-09-25'`: o próprio dia ficaria de
 * fora.
 */
const MetricChart: React.FC<MetricChartProps> = ({
  patientId,
  parameter,
  config,
  thresholds,
  anchor,
  fallback,
}) => {
  const [items, setItems] = useState<GraphData[]>();
  const [failed, setFailed] = useState(false);

  const { startDate, endDate } = useMemo(() => {
    const end = startOfDay(addDays(new Date(), 1));
    const start = startOfDay(subDays(parseISO(anchor), WINDOW_DAYS));

    return { startDate: start, endDate: end };
  }, [anchor]);

  useCurrentEffect(
    (isCurrent) => {
      getMetricValuesByRange({
        patientId,
        metricType: config.metricType,
        startDate,
        endDate,
      })
        .then((data) => {
          if (isCurrent()) {
            setItems(data);
          }
        })
        .catch(() => {
          if (isCurrent()) {
            setFailed(true);
            setItems([]);
          }
        });
    },
    [patientId, config.metricType, startDate, endDate],
  );

  const limits = useMemo(
    () =>
      config.series.flatMap((series) =>
        limitLines(thresholds, parameter, series),
      ),
    [config.series, thresholds, parameter],
  );

  const chart = useMemo(() => {
    if (!items) {
      return null;
    }

    const fromAlert = items.length === 0;
    const source = fromAlert
      ? fallback.map((trigger) => triggerAsMetric(trigger, config))
      : items;

    const points = source
      .map((item) => ({
        at: parseISO(item.mensuramentDate),
        values: config.series.map((series) => series.read(item)),
      }))
      .filter((point) => point.values.some((value) => value !== null))
      .sort((a, b) => a.at.getTime() - b.at.getTime());

    if (points.length === 0) {
      return null;
    }

    const header = [
      { type: 'datetime', label: 'Data' },
      ...config.series.map((series) => series.label),
      ...limits.map((limit) => limit.label),
    ];

    // Os limites atravessam a janela inteira, não só o trecho com leitura: com uma
    // leitura só, eles viravam pontos soltos em vez de linhas.
    const edge = (at: Date) => [
      at,
      ...config.series.map(() => null),
      ...limits.map((limit) => limit.value),
    ];

    // O eixo termina na leitura mais recente (ou na do alerta), não no fim da busca:
    // estendido até amanhã, dados de dias atrás ficavam espremidos à esquerda.
    const lastAt = Math.max(
      points[points.length - 1].at.getTime(),
      parseISO(anchor).getTime(),
    );
    const axisEnd = new Date(lastAt + 6 * 3600e3);

    const rows = [
      edge(startDate),
      ...points.map((point) => [
        point.at,
        ...point.values,
        ...limits.map((limit) => limit.value),
      ]),
      edge(axisEnd),
    ];

    // A escala cobre as leituras **e** os limites: sem os limites, um paciente todo
    // dentro da faixa teria o gráfico recortado sem mostrar onde ela termina.
    const all = [
      ...points.flatMap((point) =>
        point.values.filter((value): value is number => value !== null),
      ),
      ...limits.map((limit) => limit.value),
    ];
    const min = Math.min(...all);
    const max = Math.max(...all);
    const pad = Math.max((max - min) * 0.1, 1);

    const seriesOptions: Record<number, Record<string, unknown>> = {};

    config.series.forEach((_, index) => {
      seriesOptions[index] = {
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        lineWidth: 2,
        pointSize: 5,
      };
    });

    limits.forEach((limit: LimitLine, index) => {
      seriesOptions[config.series.length + index] = {
        color: getAlertLabels.severityColor(limit.severity),
        lineWidth: 1.5,
        lineDashStyle: [6, 4],
        pointSize: 0,
        enableInteractivity: false,
      };
    });

    return {
      data: [header, ...rows],
      options: {
        // A legenda do Google pagina ('1/2') quando não cabe, e esconde justamente
        // os limites. A legenda é a nossa, em HTML, que quebra linha.
        legend: { position: 'none' },
        chartArea: { left: 48, right: 16, top: 16, bottom: 48 },
        hAxis: {
          format: config.hAxisFormat ?? 'dd/MM HH:mm',
          textStyle: { fontSize: 11 },
        },
        vAxis: {
          viewWindow: {
            min: Math.floor(min - pad),
            max: Math.ceil(max + pad),
          },
          textStyle: { fontSize: 11 },
        },
        interpolateNulls: true,
        series: seriesOptions,
      },
      count: points.length,
      axisEnd,
      fromAlert,
    };
  }, [items, fallback, config, limits, startDate, anchor]);

  // Carregando com a altura do gráfico: a abertura anima numa ida só, em vez de
  // parar na altura do spinner e crescer de novo quando os dados chegam.
  if (!items) {
    return (
      <Loading>
        <Spinner />
      </Loading>
    );
  }

  const period = `${format(startDate, 'dd/MM')} a ${format(
    chart ? chart.axisEnd : subDays(endDate, 1),
    'dd/MM',
  )}`;

  if (!chart) {
    return (
      <Empty>
        {failed
          ? 'Não foi possível carregar as leituras.'
          : `Nenhuma leitura de ${period}.`}
      </Empty>
    );
  }

  return (
    <Wrapper>
      <Caption>
        {chart.fromAlert
          ? `${chart.count} ${
              chart.count === 1 ? 'leitura' : 'leituras'
            } do alerta (sem leituras do dispositivo de ${period})`
          : `${chart.count} leituras de ${period}`}
      </Caption>
      <Chart
        chartType="LineChart"
        chartLanguage="pt-BR"
        width="100%"
        height="250px"
        loader={<Spinner />}
        data={chart.data}
        options={chart.options}
      />
      <Legend>
        {config.series.map((series, index) => (
          <LegendItem key={series.label}>
            <Swatch color={SERIES_COLORS[index % SERIES_COLORS.length]} />
            {series.label} ({config.unit})
          </LegendItem>
        ))}
        {limits.map((limit) => (
          <LegendItem key={limit.label}>
            <Swatch
              color={getAlertLabels.severityColor(limit.severity)}
              dashed
            />
            {limit.label}
          </LegendItem>
        ))}
      </Legend>
    </Wrapper>
  );
};

export default MetricChart;
